import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Sparkles, CreditCard, Tent, TrendingUp, Users, Shield, Loader2, ArrowRight, Calendar } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SettingsContext";

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const subTitle = settings?.sub_title || "Publiez vos terrains et accueillez des campeurs";
  const subSubtitle = settings?.sub_subtitle || "Un seul abonnement annuel pour publier tous vos terrains, recevoir des réservations et developper vos revenus.";
  const [subscription, setSubscription] = useState({ status: "inactive", expires: "", price: 10 });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  // Optional redirect param: ?next=/create-space
  const params = new URLSearchParams(location.search);
  const nextPath = params.get("next") || "/create-space";
  const isLocked = params.get("locked") === "1";

  useEffect(() => {
    if (!user) { navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`); return; }
    api.get("/subscription/status")
      .then(r => setSubscription(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate, location]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      if (data.free_mode) {
        toast.success(data.message || "Abonnement active!");
        const r = await api.get("/subscription/status");
        setSubscription(r.data);
        // Auto-redirect to next page after subscribing
        setTimeout(() => navigate(nextPath), 1200);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubscribing(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  const isActive = subscription.status === "active";
  const benefits = [
    { icon: Tent, title: "Publier autant de terrains que vous voulez", desc: "Aucune limite sur le nombre d'annonces" },
    { icon: Users, title: "Recevoir des demandes de réservation", desc: "Acceptez ou refusez selon votre disponibilite" },
    { icon: CreditCard, title: "Paiements securises via Stripe", desc: "Les fonds arrivent directement sur votre compte" },
    { icon: TrendingUp, title: "Accès au programme Hôte Superstar", desc: "Gagnez plus de visibilite avec les meilleures notes" },
    { icon: Shield, title: "Support prioritaire", desc: "Une equipe dediee pour vous aider" },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-emerald-50/40 via-white to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {isLocked && !isActive && (
          <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex gap-3" data-testid="locked-banner">
            <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">Publier un terrain requiert un abonnement</p>
              <p className="text-sm text-amber-700 mt-1">Abonnez-vous pour 10 $/an et commencez a accueillir des campeurs.</p>
            </div>
          </div>
        )}

        {isActive ? (
          <Card className="border-2 border-emerald-200 rounded-3xl bg-gradient-to-br from-emerald-50 to-white shadow-sm" data-testid="subscription-active-card">
            <CardContent className="p-8 text-center">
              <div className="h-14 w-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-zinc-950 mb-2">Abonnement actif</h1>
              <p className="text-zinc-500 mb-4">Vous pouvez publier et gerer vos terrains sans limite.</p>
              {subscription.expires && (
                <div className="inline-flex items-center gap-2 text-sm text-zinc-700 bg-white px-4 py-2 rounded-full border border-zinc-200">
                  <Calendar className="h-4 w-4 text-emerald-600" />
                  <span>Valide jusqu'au <strong>{format(new Date(subscription.expires), "d MMMM yyyy", { locale: fr })}</strong></span>
                </div>
              )}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/create-space"><Button className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl h-11 px-6" data-testid="go-create-btn">Publier un terrain <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
                <Link to="/my-spaces"><Button variant="outline" className="rounded-xl h-11 px-6">Mes terrains</Button></Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="text-center mb-8">
              <Badge className="mb-4 bg-emerald-600 text-white border-0 px-3 py-1">Propriétaires</Badge>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-zinc-950 mb-3 leading-tight">
                {subTitle}
              </h1>
              <p className="text-zinc-600 max-w-xl mx-auto">
                {subSubtitle}
              </p>
            </div>

            <Card className="border-2 border-emerald-600/20 rounded-3xl overflow-hidden shadow-lg mb-6" data-testid="subscription-plan-card">
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-6">
                <p className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">Abonnement annuel</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-6xl font-black">{subscription.price}</span>
                  <span className="text-2xl font-bold">$</span>
                  <span className="text-sm opacity-80 ml-1">/ an</span>
                </div>
                <p className="text-sm opacity-90 mt-1" data-testid="subscription-monthly-label">{(() => {
                  const monthly = (Number(subscription.price) || 0) / 12;
                  const ceiled = Math.ceil(monthly * 2) / 2; // arrondi au 0.50 superieur
                  const formatted = ceiled % 1 === 0 ? `${ceiled.toFixed(0)} $` : `${ceiled.toFixed(2)} $`;
                  return `Moins de ${formatted} par mois`;
                })()}</p>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-3 mb-6">
                  {benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
                      <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <b.icon className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-zinc-900">{b.title}</p>
                        <p className="text-xs text-zinc-500">{b.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="w-full bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl h-12 text-base font-semibold"
                  data-testid="subscribe-btn"
                >
                  {subscribing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Activation...</>) : (<>M'abonner pour {subscription.price} $ / an <ArrowRight className="h-4 w-4 ml-2" /></>)}
                </Button>
                <p className="text-[11px] text-zinc-400 text-center mt-3">
                  Sans engagement - annulable a tout moment - paiement sécurisé par Stripe
                </p>
              </CardContent>
            </Card>

            <p className="text-center text-xs text-zinc-400">
              Une question ? <Link to="/" className="text-emerald-700 hover:underline">Contactez-nous</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
